import type { Profile } from "@/hooks/useAuth";
import { memberSinceText } from "@/lib/profile";
import StarRating from "@/components/feature/StarRating";
import type { RatingSummary } from "@/lib/reviews";

interface ProfileHeaderProps {
  profile: Profile;
  counts: { published: number; sold: number; favorites: number };
  rating: RatingSummary;
  onChangeCover: () => void;
  onChangeAvatar: () => void;
}

const SELLER_STATUS_LABEL: Record<string, string> = {
  none: "Comprador",
  pending: "Vendedor pendiente",
  under_review: "En revisión",
  approved: "Vendedor aprobado",
  rejected: "Rechazado",
  suspended: "Suspendido",
};

function initials(name: string | null, email: string | null): string {
  const source = (name && name.trim()) || email || "U";
  return source.charAt(0).toUpperCase();
}

export default function ProfileHeader({
  profile,
  counts,
  rating,
  onChangeCover,
  onChangeAvatar,
}: ProfileHeaderProps) {
  const member = memberSinceText(profile.created_at);
  const isVerifiedMember = profile.verified_status === "verified";
  const sellerLabel =
    SELLER_STATUS_LABEL[profile.seller_status] ?? profile.seller_status;

  return (
    <div>
      {/* Portada (ancho completo, mismo tamaño que el perfil público) */}
      <div className="group relative h-48 w-full overflow-hidden bg-background-100 md:h-64">
        {profile.cover_image ? (
          <img
            src={profile.cover_image}
            alt="Portada del perfil"
            className="h-full w-full object-cover object-center"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-background-100 to-background-200 text-foreground-300">
            <i className="ri-image-line text-5xl"></i>
          </div>
        )}

        <button
          type="button"
          onClick={onChangeCover}
          className="absolute right-3 top-3 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-black/50 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition-opacity hover:bg-black/70 sm:opacity-0 sm:group-hover:opacity-100"
        >
          <i className="ri-camera-line"></i>
          Cambiar portada
        </button>
      </div>

      {/* Cuerpo del encabezado: superpuesto sobre la portada */}
      <div className="mx-auto -mt-20 w-full max-w-5xl px-4 md:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          {/* Avatar + nombre */}
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end">
            <div className="relative">
              <div className="h-28 w-28 shrink-0 overflow-hidden rounded-full border-4 border-background-50 bg-background-100">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.full_name ?? "Avatar"}
                    className="h-full w-full object-cover object-top"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-background-100 text-4xl font-bold text-foreground-400">
                    {initials(profile.full_name, profile.email)}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onChangeAvatar}
                className="absolute bottom-1 right-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70"
                aria-label="Cambiar foto"
              >
                <i className="ri-camera-line text-sm"></i>
              </button>
            </div>

            <div className="pb-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-2xl font-bold text-foreground-950">
                  {profile.full_name || "Sin nombre"}
                </h1>
                {isVerifiedMember && (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-white">
                    <i className="ri-verified-badge-fill text-base"></i>
                  </span>
                )}
                {!isVerifiedMember && profile.seller_verified && (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-500 text-white">
                    <i className="ri-check-line text-base"></i>
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-sm text-foreground-500">
                {profile.username
                  ? `@${profile.username}`
                  : "Sin nombre de usuario"}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700">
                  <i className="ri-calendar-line"></i>
                  Miembro desde {member.date}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  {sellerLabel}
                </span>
                {rating.count > 0 ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                    <StarRating value={rating.average} size="sm" />
                    {rating.average.toFixed(1)} · {rating.count} valoraciones
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
                    <i className="ri-star-line"></i>
                    Sin valoraciones
                  </span>
                )}
                {profile.show_location && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700">
                    <i className="ri-map-pin-line"></i>
                    {[profile.city, profile.province, profile.country]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Estadísticas */}
          <div className="flex items-center gap-6 pb-1">
            <div className="text-center">
              <p className="font-heading text-xl font-bold text-foreground-950">
                {counts.published}
              </p>
              <p className="text-xs text-foreground-500">Publicaciones</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-xl font-bold text-foreground-950">
                {counts.sold}
              </p>
              <p className="text-xs text-foreground-500">Vendidos</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-xl font-bold text-foreground-950">
                {counts.favorites}
              </p>
              <p className="text-xs text-foreground-500">Favoritos</p>
            </div>
          </div>
        </div>

        {profile.bio && (
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700">
            {profile.bio}
          </p>
        )}
      </div>
    </div>
  );
}